import { Form } from "antd";
import { useState, useEffect } from "react";
import {
  deletePointsRule,
  updatePointsRule,
  addPointsRule,
} from "../repository/data.repository";
import { PointsRule } from "../repository/data.default";
import DynamicForm from "../controls/DynamicForm";
import {
  EngagementEventType,
  PointsRulesForm,
} from "./PointsRulesView.default";
import InModal from "../controls/InModal";
import { modalTitle } from "../controls/InModal.default";
import ModalFooter from "../controls/ModalFooter";
import ModalDeleteConfirmation from "../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../controls/ModalErrorAlert";
import { getProblemMessage } from "../utils/http";
import { enumRequest } from "../utils/options";

export interface PointsRuleFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: PointsRule;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function PointsRuleFormDialog(props: PointsRuleFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<PointsRulesForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (props.open) {
      form.setFieldsValue({
        ...props.initialValues,
        eventType: props.initialValues?.eventType
          ? {
              value: props.initialValues.eventType,
              display: props.initialValues.eventType,
            }
          : undefined,
      });
    } else {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then((value) => setValid(!!Object.values(value).length))
      .catch(() => setValid(false));
  }, [form, values, props.open]);

  function onError(error: unknown): void {
    setLoading(false);
    setErrorMessage(getProblemMessage(error, "Something went wrong, try again"));
  }

  function onSubmit(): void {
    const { eventType, points, isActive, description } = values;
    setLoading(true);
    setErrorMessage(null);
    if (props.mode === "add") {
      addPointsRule({
        eventType: eventType?.value as string,
        points: points ?? 0,
        isActive: isActive ?? true,
        description,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else if (props.mode === "delete") {
      deletePointsRule(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else if (props.mode === "update") {
      updatePointsRule({
        uid: props.initialValues?.uid,
        rowVersion: props.initialValues?.rowVersion,
        eventType: eventType?.value as string,
        points,
        isActive,
        description,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else {
      setLoading(false);
      props.onFinish?.();
    }
  }

  function reset(): void {
    setValid(false);
    setErrorMessage(null);
    setLoading(false);
    form.resetFields();
  }

  const schemaControls = [
    {
      type: "combobox" as const,
      name: "eventType",
      label: "Event Type",
      request: enumRequest(EngagementEventType),
      rules: [{ required: true, message: "Event Type is required" }],
    },
    {
      type: "numberbox" as const,
      name: "points",
      label: "Points",
      rules: [{ required: true, message: "Points are required" }],
    },
    {
      type: "togglebox" as const,
      name: "isActive",
      label: "Active",
      checkedText: "Yes",
      unCheckedText: "No",
    },
    {
      type: "textbox" as const,
      name: "description",
      label: "Description (optional)",
    },
  ];

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(
        props.mode,
        "Points Rule",
        props.initialValues?.eventType,
      )}
      onCancel={props.onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={props.onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {(props.mode === "add" || props.mode === "update") && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{ groups: [{ controls: schemaControls }] }}
        />
      )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="points rule"
          entityName={props.initialValues?.eventType}
        />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default PointsRuleFormDialog;
